import { z } from 'zod';

/**
 * PAN guard — reject full primary account numbers (docs/00 §4, Task 3).
 * Digits-only sequences of length 13–19 that pass Luhn are treated as PANs.
 * Display suffixes (3–8 digits) are never PANs.
 */

export const PAN_REJECTED = 'PAN_REJECTED';

export function luhnOk(digits: string): boolean {
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i -= 1) {
    let n = Number(digits.charAt(i));
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
}

/** True if `value` contains a Luhn-valid 13–19 digit PAN (spaces/dashes ignored). */
export function containsPan(value: string): boolean {
  const compact = value.replace(/[\s-]/g, '');
  // Scan digit runs inside mixed text.
  const runs = compact.match(/\d{13,19}/g);
  if (!runs) return false;
  return runs.some((run) => luhnOk(run));
}

export function assertNoPan(value: string): void {
  if (containsPan(value)) {
    throw new Error(PAN_REJECTED);
  }
}

/** Zod string that rejects embedded PANs. */
export const panFreeString = z.string().superRefine((val, ctx) => {
  if (containsPan(val)) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: PAN_REJECTED });
  }
});

export const panFreeStringNull = panFreeString.nullable();
